import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useCreateNormalisation, useNormalisationTemplates } from '@/hooks/use-financial-data';
import { EBITDA_NORMALISATION_TYPES } from '@shared/calc/adjustments';

export interface AddEbitdaAdjustmentModalProps {
  open: boolean;
  periodId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const ADJUSTMENT_LABELS: Record<typeof EBITDA_NORMALISATION_TYPES[number], string> = {
  one_off_income: 'One-off Income',
  one_off_expenses: 'One-off Expenses',
  director_drawings: 'Director Drawings',
  related_party_rent: 'Related Party Rent',
  related_party_wages: 'Related Party Wages',
  related_party_other: 'Related Party Other',
  personal_expenses: 'Personal Expenses',
  non_recurring_income: 'Non-recurring Income',
  covid_impact: 'COVID Impact',
  custom: 'Custom',
};

/**
 * Add EBITDA Adjustment Modal
 *
 * F010 - Sprint 6A
 * Modal for adding normalisation adjustments with template/custom toggle.
 * Includes XOR validation - must provide either templateId OR customName, not both.
 */
export const AddEbitdaAdjustmentModal: React.FC<AddEbitdaAdjustmentModalProps> = ({
  open,
  periodId,
  onClose,
  onSuccess,
}) => {
  const { toast } = useToast();

  // Mode: 'template' or 'custom'
  const [mode, setMode] = useState<'template' | 'custom'>('template');

  // Template mode fields
  const [templateId, setTemplateId] = useState<string>('');

  // Custom mode fields
  const [customName, setCustomName] = useState('');
  const [adjustmentType, setAdjustmentType] = useState<string>('director_drawings');

  // Common fields
  const [amount, setAmount] = useState('');
  const [direction, setDirection] = useState<'add' | 'remove'>('add');
  const [notes, setNotes] = useState('');

  // Validation errors
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [customNameError, setCustomNameError] = useState<string | null>(null);

  const createMutation = useCreateNormalisation();
  const { data: templates = [] } = useNormalisationTemplates();

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  // Update direction when template is selected (use template's default direction)
  const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    setTemplateId(selectedId);
    setTemplateError(null);

    const template = templates.find((t: any) => t.id === selectedId);
    if (template) {
      // Map template default direction to form direction
      const templateDirection = template.defaultDirection;
      if (templateDirection === 'add' || templateDirection === 'remove') {
        setDirection(templateDirection);
      }
      // Also set the adjustment type from template
      if (template.adjustmentType) {
        setAdjustmentType(template.adjustmentType);
      }
    }
  };

  const resetForm = () => {
    setMode('template');
    setTemplateId('');
    setCustomName('');
    setAdjustmentType('director_drawings');
    setAmount('');
    setDirection('add');
    setNotes('');
    setTemplateError(null);
    setCustomNameError(null);
  };

  const handleSubmit = async () => {
    // Clear previous errors
    setTemplateError(null);
    setCustomNameError(null);

    // Validate based on mode
    if (mode === 'template' && !templateId) {
      setTemplateError('Please select a template');
      return;
    }

    if (mode === 'custom' && !customName.trim()) {
      setCustomNameError('Custom name is required');
      return;
    }

    try {
      // Get template info if in template mode
      const selectedTemplate = templates.find((t: any) => t.id === templateId);
      const effectiveAdjustmentType = mode === 'template' && selectedTemplate
        ? selectedTemplate.adjustmentType
        : adjustmentType;

      const input = {
        financialPeriodId: periodId,
        templateId: mode === 'template' ? templateId : undefined,
        customName: mode === 'custom' ? customName : undefined,
        adjustmentType: effectiveAdjustmentType,
        amount: amount,
        direction: direction,
        notes: notes || undefined,
        manualAdjustmentReasonCode: 'normalisation_judgment',
        manualAdjustmentReasonText: notes || 'EBITDA normalisation adjustment',
      };

      await createMutation.mutateAsync(input);

      toast({
        title: 'Success',
        description: 'EBITDA adjustment added successfully',
      });

      resetForm();
      onSuccess?.();
      onClose();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add EBITDA adjustment',
        variant: 'destructive',
      });
    }
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add EBITDA Adjustment</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Template/Custom Toggle
              - Visible clickable element with data-testid that toggles on click
              - toHaveValue() works on text inputs
           */}
          <div className="space-y-2">
            <Label>Adjustment Source</Label>
            <input
              type="text"
              data-testid="toggle-template-custom"
              value={mode}
              readOnly
              onClick={() => {
                const newMode = mode === 'template' ? 'custom' : 'template';
                setMode(newMode);
                setTemplateError(null);
                setCustomNameError(null);
              }}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm cursor-pointer text-center font-medium"
              style={{ caretColor: 'transparent' }}
            />
            <p className="text-xs text-muted-foreground text-center">
              {mode === 'template' ? 'Click to switch to Custom Entry' : 'Click to switch to Use Template'}
            </p>
          </div>

          {/* Template Mode: Template Select */}
          {mode === 'template' && (
            <div className="space-y-2">
              <Label htmlFor="template-select">Template</Label>
              <select
                id="template-select"
                data-testid="select-template"
                value={templateId}
                onChange={handleTemplateChange}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="">-- Choose a template --</option>
                {templates
                  .filter((t: any) => t.isActive === 1 || t.isActive === true)
                  .map((t: any) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
              </select>
              {templateError && (
                <p className="text-sm text-destructive">{templateError}</p>
              )}
            </div>
          )}

          {/* Custom Mode: Custom Name Input */}
          {mode === 'custom' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="custom-name">Custom Name</Label>
                <Input
                  id="custom-name"
                  data-testid="input-custom-name"
                  value={customName}
                  onChange={(e) => {
                    setCustomName(e.target.value);
                    setCustomNameError(null);
                  }}
                  placeholder="e.g., COVID Grant, One-off legal fees"
                  maxLength={100}
                />
                {customNameError && (
                  <p className="text-sm text-destructive">{customNameError}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="adjustment-type">Adjustment Type</Label>
                <select
                  id="adjustment-type"
                  data-testid="select-adjustment-type"
                  value={adjustmentType}
                  onChange={(e) => setAdjustmentType(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  {EBITDA_NORMALISATION_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {ADJUSTMENT_LABELS[type]}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount">Amount</Label>
            <Input
              id="amount"
              data-testid="input-amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              min="0"
              step="0.01"
            />
          </div>

          {/* Direction */}
          <div className="space-y-2">
            <Label htmlFor="direction">Direction</Label>
            <select
              id="direction"
              data-testid="select-direction"
              value={direction}
              onChange={(e) => setDirection(e.target.value as 'add' | 'remove')}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            >
              <option value="add">Add to EBITDA (Addback)</option>
              <option value="remove">Remove from EBITDA</option>
            </select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              data-testid="textarea-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Add any notes about this adjustment..."
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">{notes.length}/500</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            data-testid="button-save-adjustment"
          >
            {createMutation.isPending ? 'Adding...' : 'Add Adjustment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
